import RadioField from '@/core/components/forms/fields/radio-field';
import { useManagementMandateStore } from '@/store/management-mandate';
import { get } from 'lodash';
import { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

interface Option {
  label: string;
  value: string;
  isOther?: boolean;
}

interface Question {
  question: string;
  options: Option[];
  image?: string;
  title: string;
  name: string;
}

interface FormProps {
  onNext: () => void,
  onBack: () => void,
  dataQuestion: any,
  isStart: boolean,
  isLoading: boolean
}

const getCurrentStep = (questions: Question[], questionnaire: any, MAX_STEP: number) => {
  const index = questions.findIndex((question: Question) => !get(questionnaire, question.name, ''));
  return index >= 0 ? index : Object.keys(questionnaire).length ? MAX_STEP : 0;
}

const QuestionnaireForm = ({ onNext, onBack, dataQuestion, isStart, isLoading }: FormProps) => {
  const { t } = useTranslation();
  const { questions, description } = dataQuestion;
  const MAX_STEP = questions.length - 1;
  const questionnaire = useManagementMandateStore((state) => state.questionnaire);
  const setQuestionnaire = useManagementMandateStore((state) => state.setQuestionnaire);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const [step, setStep] = useState<number>(
    isStart ? 0 
      : getCurrentStep(questions, questionnaire, MAX_STEP)
  );

  const handleChange = (name: string, value: string, isOther?: boolean) => {
    if (get(questionnaire, name) !== value) {
      
      // If check option that is not specify, reset the value
      const isOtherIndex = memoizedQuestions.options.findIndex((option: Option) => option.isOther);
      if (isOtherIndex >= 0 && !isOther) {
        setQuestionnaire({ ...questionnaire, [`${name}_specify`]: '', [name]: value });
      } else {
        setQuestionnaire({ ...questionnaire, [name]: value });
      }

      // Clear the previous timeout if it exists
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      // Set a new timeout
      timeoutRef.current = setTimeout(() => {
        handleOnNext();
      }, 2000);

      if (isOther) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null; // Reset ref
      }
    }
  }

  const handleOnBack = () => {
    if (step === 0) {
      onBack();
      return;
    }
    setStep(step - 1);
  }

  const handleOnNext = () => {
    if (step === MAX_STEP) {
      onNext();
      return;
    }
    setStep(step + 1);
  }

  const memoizedQuestions = useMemo(() => questions[step], [questions, step]);
  
  return (
    <>
      <div className="management-mandate__wrapper__content__step">{t('common.step')} {step + 1}/{questions.length}</div>
      <h2 className="management-mandate__wrapper__content__title">{t(description)}</h2>
      <div className="questionnaire-form">
        <div>
          {memoizedQuestions.title && <div className="question-title">{t(memoizedQuestions.title)}</div>}
          <br />
          <div className="question-title">{t(memoizedQuestions.question)}</div>
          {
            memoizedQuestions.image && (
              <div className="question-image">
                <img
                  src={memoizedQuestions.image}
                  className="question-image__img"
                  alt=""
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                />
              </div>
            )
          }
          <div className="question-options">
            {
              memoizedQuestions.options.map((option: Option, key: number) => {
                return (
                  <div key={`${memoizedQuestions.name}-${option.value}-${key}`} style={{ width: '100%' }}>
                    <RadioField
                      checked={get(questionnaire, `${memoizedQuestions.name}`, '') === option.value}
                      label={t(option.label)}
                      value={option.value}
                      onChange={(value: string) => handleChange(memoizedQuestions.name, value, option.isOther)}
                    />
                    {
                      option.isOther && (
                        <textarea
                          maxLength={250}
                          placeholder={t('form.required.max_250_characters')}
                          value={get(questionnaire, `${memoizedQuestions.name}_specify`, '')}
                          onChange={(e) => handleChange(`${memoizedQuestions.name}_specify`, e.target.value, option.isOther)}
                          disabled={get(questionnaire, memoizedQuestions.name, '') !== option.value}
                        />
                      )
                    }
                  </div>
                )
              })
            }
          </div>
        </div>
      </div>
      <div className="dv-form__footer">
        <button type='submit' className="cta-button cta-medium cta-grey" onClick={handleOnBack}>
          {t('common.back')}
        </button>
        <button disabled={!get(questionnaire, `${memoizedQuestions.name}`, '') || isLoading} type='submit' className="cta-button cta-medium cta-blue" onClick={handleOnNext}>
          {t('common.next')}
        </button>
      </div>
    </>
  )
}

export default QuestionnaireForm;